import JSONModel from "sap/ui/model/json/JSONModel";
import BaseController from "../controller/BaseController";
import Event from "sap/ui/base/Event";
import { EnvironmentService } from "../service/Environment.service";
import Log from "sap/base/Log";
import MessageBox from "sap/m/MessageBox";
import { Link$PressEvent } from "sap/m/Link";
import { URLHelper } from "sap/m/library";
import Dialog from "sap/m/Dialog";
import { Button$ClickEvent } from "sap/ui/webc/main/Button";
import { Button$PressEvent } from "sap/m/Button";
import SimpleForm from "sap/ui/layout/form/SimpleForm";
import { ListItemBase$PressEvent } from "sap/m/ListItemBase";
import { FormValidator } from "../service/FormValidator.service";
import { ErrorHandler } from "../service/ErrorHandler.service";
import { Select$ChangeEvent } from "sap/m/Select";

/**
 * @namespace com.unitrade.crm.blocks
 */
export default class ContactsBlock extends BaseController {
  private _contactsModels: JSONModel;
  private _metaModel: JSONModel;
  private _selectedContact: JSONModel;
  private _oDetailsDialog: Dialog;

  onInit(): void {
    this.onModelContextChange = this.onModelContextChange.bind(this);
    this._contactsModels = new JSONModel();
    this._metaModel = new JSONModel({ busy: false });
    this._selectedContact = new JSONModel();
    this.setModel(this._contactsModels);
    this.setModel(this._selectedContact, "selectedContact");
    this.setModel(this._metaModel, "meta");
    this.getView().attachModelContextChange(null, this.onModelContextChange);
    this.onAddContactButtonClick = this.onAddContactButtonClick.bind(this);
  }

  onExit(): void {
    this.getView().detachModelContextChange(this.onModelContextChange);
  }

  async onModelContextChange(oEvent: Event) {
    await this._loadContacts();
  }

  private async _loadContacts() {
    this._metaModel.setProperty("/busy", true);
    const oCustomerModel = this.getModel("Customer");

    if (oCustomerModel) {
      try {
        this._contactsModels.setData(null);
        const kundId = oCustomerModel.getProperty("/id");
        const kundLfd = oCustomerModel.getProperty("/lfd");
        if (!!kundId && !!kundLfd) {
          const queryUrl = `${EnvironmentService.getBaseUrl()}/kontakte/kunde/${kundId}/${kundLfd}`;
          Log.debug("Loading Kontakte", `Kunde ${kundId}/${kundLfd}`, "ContactsBlock");
          await this._contactsModels.loadData(queryUrl);
        }
      } catch (ex: any) {
        ErrorHandler.handleError(ex, { title: "Fehler beim Laden der Kontakte", ignorable: true, onRetry: this._loadContacts });
      } finally {
        this._metaModel.setProperty("/busy", false);
      }
    }
  }

  onAddContactButtonClick = async (oEvent: Button$ClickEvent) => {
    const oCustomerModel = this.getModel("Customer");
    const kundId = oCustomerModel.getProperty("/id");
    const kundLfd = oCustomerModel.getProperty("/lfd");
    this._selectedContact.setData({
      id: 0,
      kundeId: kundId,
      kundeLfd: kundLfd,
    });

    this._openDetailsDialog();
  };

  private async _openDetailsDialog() {
    if (!this._oDetailsDialog) {
      this._oDetailsDialog = (await this.loadFragment({ name: "com.unitrade.crm.fragments.ContactChangeDialog" })) as Dialog;
    }
    this._oDetailsDialog.open();
  }

  dialogStatusChange = (oEvent: Select$ChangeEvent) => {
    const status = oEvent.getParameter("selectedItem");
    if (status && status.getKey() === "0") {
      MessageBox.warning(
        "Beim Setzen eines Kontakts auf den Status 'Inaktiv' ist dieser nach dem Speichern in CRMmobile nicht mehr verfügbar und kann nur noch in der U7 bearbeitet werden."
      );
    }
  };

  dialogCancelPress = (oEvent: Button$PressEvent) => {
    if (this._oDetailsDialog) {
      this._oDetailsDialog.close();
    }
  };

  dialogSavePress = async (oEvent: Button$PressEvent) => {
    const validationError = this._validateForm();

    if (!validationError) {
      const contact = this._selectedContact.getData();
      try {
        this._metaModel.setProperty("/busy", true);
        contact.id > 0 ? await this._updateContact(contact) : await this._createContact(contact);
        this._oDetailsDialog.close();
      } catch (oException: any) {
        MessageBox.error("Fehler beim Speichern von Kontakt: " + oException.statusText);
        Log.error("Error saving contact", oException, "ContactsBlock");
      } finally {
        this._metaModel.setProperty("/busy", false);
      }
      await this._loadContacts();
    }
  };

  private _createContact = async (contact: any) => {
    const url = `${EnvironmentService.getBaseUrl()}/kontakte/kunde/${contact.kundeId}/${contact.kundeLfd}`;
    await $.post({
      url: url,
      data: JSON.stringify(contact),
    });
  };

  private _updateContact = async (contact: any) => {
    const url = `${EnvironmentService.getBaseUrl()}/kontakte/kunde/${contact.kundeId}/${contact.kundeLfd}/${contact.id}`;
    await $.ajax({
      method: "PUT",
      url: url,
      data: JSON.stringify(contact),
    });
  };

  private _validateForm(): boolean {
    const oForm = this.byId("contact-change-dialog-fragment-form") as SimpleForm;
    return FormValidator.validate(oForm);
  }

  handlePhonePress = (oEvent: Link$PressEvent) => {
    URLHelper.triggerTel(oEvent.getSource().getText());
  };

  handleEmailPress = (oEvent: Link$PressEvent) => {
    URLHelper.triggerEmail(oEvent.getSource().getText());
  };

  onContactPress = (oEvent: ListItemBase$PressEvent) => {
    const oItem = oEvent.getSource();
    const oObject = oItem.getBindingContext().getObject();
    this._selectedContact.setData(oObject);

    this._openDetailsDialog();
  };

  onParentBlockModeChange = (oEvent: any) => {
    // do nothing (for now)
  };
}
